import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PenTool, X } from 'lucide-react';
import type { MeasurementSketchRef } from '@tailoring-crm/shared';
import { Button, Label } from '@tailoring-crm/ui';
import { MeasurementSketchDialog } from './measurement-sketch-dialog.js';
import { SketchPicture } from './sketch-picture.js';

/**
 * The sketch part of a form: the picture (or an empty frame), a Draw / Redraw button that opens
 * the full-screen canvas, and Remove. Controlled — it holds no sketch of its own, it reports a
 * new reference through `onChange` and the form it sits in decides when that is saved.
 */
export interface SketchFieldProps {
  garmentTypeKey: string;
  /** For the heading and alt text: "Priya — blouse". */
  name: string;
  value: MeasurementSketchRef | null;
  onChange: (value: MeasurementSketchRef | null) => void;
  /** The garment type's outline, which a first drawing opens on. */
  outline?: MeasurementSketchRef | null;
  heading?: ReactNode;
  hint?: ReactNode;
  drawLabel?: string;
  redrawLabel?: string;
  removeLabel?: string;
  disabled?: boolean;
}

export function SketchField({
  garmentTypeKey,
  name,
  value,
  onChange,
  outline = null,
  heading,
  hint,
  drawLabel,
  redrawLabel,
  removeLabel,
  disabled = false,
}: SketchFieldProps): ReactElement {
  const { t } = useTranslation();
  const [drawing, setDrawing] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{heading ?? t('measurementSketch.heading')}</Label>
      <p className="text-sm text-muted-foreground">
        {hint ??
          (outline && !value
            ? t('measurementSketch.outlineStartHint')
            : t('measurementSketch.hint'))}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {value ? (
          <SketchPicture previewMediaId={value.previewMediaId} name={name} />
        ) : (
          <span className="flex size-24 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-center text-xs text-muted-foreground">
            {t('measurementSketch.empty')}
          </span>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => setDrawing(true)}
          >
            <PenTool aria-hidden="true" />
            {value
              ? (redrawLabel ?? t('measurementSketch.redraw'))
              : (drawLabel ?? t('measurementSketch.draw'))}
          </Button>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive"
              disabled={disabled}
              onClick={() => onChange(null)}
            >
              <X aria-hidden="true" />
              {removeLabel ?? t('measurementSketch.remove')}
            </Button>
          ) : null}
        </div>
      </div>

      {drawing ? (
        <MeasurementSketchDialog
          garmentTypeKey={garmentTypeKey}
          name={name}
          sketchId={value?.sketchId ?? null}
          outlineSketchId={outline?.sketchId ?? null}
          onSaved={onChange}
          onClose={() => setDrawing(false)}
        />
      ) : null}
    </div>
  );
}
